// frontend/src/components/documents/Anexo2Modal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  AssignmentReturn as ReturnIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const Anexo2Modal = ({ open, onClose, clientId, client, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [fetchingEquipments, setFetchingEquipments] = useState(false);
  const [reason, setReason] = useState('Solicitud de baja del servicio de Internet');
  const [observations, setObservations] = useState('Retiro de hardware por cancelación de servicio.');
  
  // Equipos devueltos (Cargados 100% automáticamente desde la BD)
  const [equipments, setEquipments] = useState([]);
  const [resultStatus, setResultStatus] = useState('DEVOLUCIÓN COMPLETA CON DESGASTE NORMAL');
  const [pendingAmount, setPendingAmount] = useState(0);

  useEffect(() => {
    if (open && clientId) {
      fetchPendingRentalEquipment();
    }
  }, [open, clientId]);

  const fetchPendingRentalEquipment = async () => {
    setFetchingEquipments(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/documents/anexo2/pending-equipment/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Mapear los equipos devueltos con estado por defecto
        const mapped = data.map(item => ({
          id: item.id,
          name: item.item_name || 'Equipo Receptor CPE / Router',
          brand: item.brand || '',
          model: item.model || '',
          serialNumber: item.serial_number || '',
          macAddress: item.mac_address || '',
          value: parseFloat(item.item_value || item.purchase_price || 200),
          isReturned: true,
          condition: 'BUENO'
        }));

        setEquipments(mapped.length > 0 ? mapped : [
          { id: null, name: 'Antena CPE Receptor Inalámbrico', brand: 'Ubiquiti', model: 'LiteBeam 5AC', serialNumber: '', macAddress: client?.mac_address || '', value: 250, isReturned: true, condition: 'BUENO' },
          { id: null, name: 'Router Wi-Fi Principal Dual Band', brand: 'TP-Link', model: 'Archer C6', serialNumber: '', macAddress: '', value: 120, isReturned: true, condition: 'BUENO' }
        ]);
        recalculateTotals(mapped);
      }
    } catch (e) {
      console.error('Error fetching rental equipment:', e);
    } finally {
      setFetchingEquipments(false);
    }
  };

  const handleEquipmentChange = (index, field, value) => {
    const updated = [...equipments];
    updated[index][field] = value;
    setEquipments(updated);
    recalculateTotals(updated);
  };

  const recalculateTotals = (items) => {
    let totalPending = 0;
    let hasDamaged = false;
    let hasUnreturned = false;

    items.forEach(eq => {
      if (!eq.isReturned || eq.condition === 'NO DEVUELTO') {
        totalPending += parseFloat(eq.value || 0);
        hasUnreturned = true;
      } else if (eq.condition === 'DAÑADO' || eq.condition === 'INUTILIZABLE') {
        totalPending += parseFloat(eq.value || 0) * 0.5; // Cobertura por daño
        hasDamaged = true;
      }
    });

    setPendingAmount(totalPending);
    if (hasUnreturned) {
      setResultStatus('DEVOLUCIÓN INCOMPLETA / EQUIPOS PENDIENTES');
    } else if (hasDamaged) {
      setResultStatus('DEVOLUCIÓN CON EQUIPOS DAÑADOS');
    } else {
      setResultStatus('DEVOLUCIÓN COMPLETA CON DESGASTE NORMAL');
    }
  };

  const handleSubmit = async (formatToPrint = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        clientId,
        reason,
        returnedEquipments: equipments,
        resultStatus,
        pendingAmount,
        observations
      };

      const res = await fetch('/api/documents/anexo2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error guardando Ficha de Devolución');

      alert('✅ ¡Ficha de Devolución (Anexo 2) registrada e inventario actualizado exitosamente!');
      
      if (formatToPrint && data.document) {
        window.open(`/api/documents/${data.document.id}/pdf?format=${formatToPrint}&token=${token}`, '_blank');
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      alert('❌ Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 800, color: '#dc2626' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReturnIcon color="error" /> Generar Ficha de Devolución por Baja (Anexo 2)
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#fff5f5' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          ℹ️ <strong>Auto-Filtrado de Inventario</strong>: El sistema consultó automáticamente los equipos propiedad de YinoWISP registrados en alquiler para <strong>{client?.first_name} {client?.last_name}</strong>. Los equipos comprados por el cliente y consumibles no requieren devolución.
        </Alert>

        <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: '10px', border: '1px solid #fca5a5' }}>
          <Typography variant="subtitle2" fontWeight="bold" color="error" sx={{ mb: 1 }}>1. REGISTRO DE RETIRO</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Abonado:</strong> {client?.first_name} {client?.last_name}</Typography>
              <Typography variant="body2"><strong>DNI / RUC:</strong> {client?.dni}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Motivo de Retiro"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Tabla Auto-Generada de Equipos Alquilados */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: '10px', border: '1px solid #fca5a5' }}>
          <Typography variant="subtitle2" fontWeight="bold" color="error" sx={{ mb: 1.5 }}>
            2. CHECKLIST DE RECEPCIÓN Y ESTADO FÍSICO DE HARDWARE
          </Typography>

          {fetchingEquipments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress color="error" /></Box>
          ) : (
            equipments.map((eq, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#fafafa', border: '1px solid #fed7d7' }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} sm={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={eq.isReturned}
                          onChange={(e) => handleEquipmentChange(idx, 'isReturned', e.target.checked)}
                          color="error"
                        />
                      }
                      label=""
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" fontWeight="bold">{eq.name}</Typography>
                    <Typography variant="caption" color="textSecondary">{eq.brand} {eq.model} | Serie/MAC: {eq.macAddress || eq.serialNumber || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Estado Físico"
                      value={eq.condition}
                      onChange={(e) => handleEquipmentChange(idx, 'condition', e.target.value)}
                    >
                      <MenuItem value="BUENO">✓ Estado Bueno (Operativo)</MenuItem>
                      <MenuItem value="DESGASTE NORMAL">✓ Desgaste Normal (Uso)</MenuItem>
                      <MenuItem value="DAÑADO">❌ Dañado por Mal Uso</MenuItem>
                      <MenuItem value="INUTILIZABLE">❌ Inutilizable / Quemado</MenuItem>
                      <MenuItem value="NO DEVUELTO">⚠️ No Devuelto (Pendiente)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" fontWeight="bold">Valor: S/ {eq.value.toFixed(2)}</Typography>
                    <Typography variant="caption" color={eq.isReturned ? 'success.main' : 'error.main'}>
                      {eq.isReturned ? ' Devuelto' : ' Pendiente'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))
          )}
        </Box>

        {/* Resultado e Importe */}
        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: '10px', border: '1px solid #fca5a5' }}>
          <Typography variant="subtitle2" fontWeight="bold" color="error" sx={{ mb: 1.5 }}>3. RESULTADO DE LIQUIDACIÓN</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={7}>
              <Typography variant="body2" fontWeight="bold">Resultado:</Typography>
              <Chip label={resultStatus} color={pendingAmount > 0 ? 'error' : 'success'} sx={{ fontWeight: 800, mt: 0.5 }} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Typography variant="body2" fontWeight="bold">Importe Pendiente por Equipos:</Typography>
              <Typography variant="h6" fontWeight={900} color={pendingAmount > 0 ? 'error.main' : 'success.main'}>
                S/ {pendingAmount.toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                label="Observaciones de Devolución"
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#fff5f5', display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="error" startIcon={<PrintIcon />} onClick={() => handleSubmit('A4')} disabled={loading}>
            Guardar e Imprimir A4
          </Button>
          <Button variant="outlined" color="error" startIcon={<PrintIcon />} onClick={() => handleSubmit('A5')} disabled={loading}>
            Imprimir Talonario A5
          </Button>
        </Box>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default Anexo2Modal;
